import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Info } from "lucide-react";

import Header from "src/components/app/layout/Header";
import BcaApis from "src/api/bca";
import Shimmer from "src/components/common/Shimmer";
import Alert from "../Alert";
import BottomSheet from "../BottomSheet";

export interface BcaInfoRecord {
  weight?: number;
  bmi?: number;
  bmr?: number;
  body_fat_percentage?: number;
  muscle_mass?: number;
  muscle_rate?: number;
  bone_mass?: number;
  hydration?: number;
  protein?: number;
  protein_rate?: number;
  visceral_fat?: number;
  metabolic_age?: number;
  fat_mass?: number;
  lean_body_mass?: number;
  [key: string]: number | undefined;
}

interface BcaRangeItem {
  from: number;
  to: number;
  label: string;
  severity_code?: number;
}

interface BcaParamMeta {
  id: string;
  name: string;
  unit: string;
  description?: string;
  ranges?: BcaRangeItem[];
}

type RangesMap = Record<
  string,
  {
    name?: string;
    unit?: string;
    description?: string;
    ranges?: BcaRangeItem[];
  }
>;

const PARAM_ORDER = [
  "weight",
  "bmi",
  "body_fat_percentage",
  "muscle_mass",
  "muscle_rate",
  "bone_mass",
  "hydration",
  "protein",
  "protein_rate",
  "visceral_fat",
  "bmr",
  "metabolic_age",
  "fat_mass",
  "lean_body_mass",
];

const FALLBACK_NAMES: Record<string, string> = {
  weight: "Weight",
  bmi: "BMI",
  bmr: "BMR",
  body_fat_percentage: "Body Fat %",
  muscle_mass: "Muscle Mass",
  muscle_rate: "Muscle Rate",
  bone_mass: "Bone Mass",
  hydration: "Hydration",
  protein: "Protein",
  protein_rate: "Protein Rate",
  visceral_fat: "Visceral Fat",
  metabolic_age: "Metabolic Age",
  fat_mass: "Fat Mass",
  lean_body_mass: "Lean Body Mass",
};

function getSeverityForValue(
  value: number,
  ranges?: BcaRangeItem[]
): { label: string; severityCode: number } {
  if (!ranges?.length) return { label: "—", severityCode: 0 };
  const idx = ranges.findIndex((r) => value >= r.from && value <= r.to);
  const i = idx !== -1 ? idx : value < ranges[0].from ? 0 : ranges.length - 1;
  const range = ranges[i];
  return {
    label: range.label,
    severityCode: range.severity_code ?? 0,
  };
}

function valueColorClass(severityCode: number): string {
  switch (severityCode) {
    case -1:
      return "text-amber-600";
    case 0:
      return "text-green-600";
    case 1:
      return "text-orange-600";
    case 2:
      return "text-red-600";
    default:
      return "text-gray-800";
  }
}

interface DeviceResultProps {
  bcaLogId: string;
  createdAt: string;
  onBackClick: () => void;
}

const DeviceResult: React.FC<DeviceResultProps> = ({
  bcaLogId,
  createdAt,
  onBackClick,
}) => {
  const [bcaInfo, setBcaInfo] = useState<BcaInfoRecord | null>(null);
  const [ranges, setRanges] = useState<RangesMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedParam, setSelectedParam] = useState<BcaParamMeta | null>(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [infoRes, rangesRes] = await Promise.all([
        BcaApis.getBcaLogInfoById({ queryParams: { bcaLogId } }),
        BcaApis.getBcaLifetronRanges({
          queryParams: { deviceType: "GoodFlip" },
        }).catch(() => null),
      ]);
      const code = infoRes?.code != null ? Number(infoRes.code) : null;
      if (code === 1 || code === 200) {
        const raw = infoRes?.data?.bca_info ?? infoRes?.data;
        setBcaInfo(raw && typeof raw === "object" ? raw : null);
      } else {
        setBcaInfo(null);
      }
      const rangesCode =
        rangesRes?.code != null ? Number(rangesRes.code) : null;
      if (rangesRes?.data && (rangesCode === 1 || rangesCode === 200)) {
        setRanges(rangesRes.data as RangesMap);
      } else {
        setRanges(null);
      }
    } catch {
      setBcaInfo(null);
      setRanges(null);
    } finally {
      setLoading(false);
    }
  }, [bcaLogId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const params = React.useMemo(() => {
    if (!bcaInfo) return [];
    return PARAM_ORDER.filter((key) => bcaInfo[key] != null).map((key) => {
      const val = Number(bcaInfo[key]);
      const meta = ranges?.[key];
      const rangeList = meta?.ranges;
      const { label, severityCode } = getSeverityForValue(val, rangeList);
      return {
        key,
        name: meta?.name ?? FALLBACK_NAMES[key] ?? key,
        unit: meta?.unit ?? "",
        description: meta?.description ?? "",
        value: val,
        rangeLabel: label,
        severityCode,
      };
    });
  }, [bcaInfo, ranges]);

  const openBottomSheet = (p: (typeof params)[0]) => {
    setSelectedParam({
      id: p.key,
      name: p.name,
      unit: p.unit,
      description: p.description,
      ranges: ranges?.[p.key]?.ranges,
    });
    setBottomSheetOpen(true);
  };

  const headerTitle = useMemo(() => {
    if (!createdAt) return "BCA Result";
    const m = moment(createdAt, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
    return m.isValid() ? m.format("DD/MM/YYYY - hh:mm A") : createdAt;
  }, [createdAt]);

  return (
    <div className="relative min-h-0 flex flex-col bg-gray-100">
      <Header
        title={headerTitle}
        titlePosition="center"
        showBackButton
        onBackClick={onBackClick}
        className="sticky top-0 left-0 right-0 z-10"
      />
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        {loading ? (
          <div className="space-y-3">
            <Shimmer width="100%" height="56px" />
            <Shimmer width="100%" height="56px" />
            <Shimmer width="100%" height="56px" />
          </div>
        ) : !bcaInfo ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            Could not load BCA details.
          </div>
        ) : (
          <div className="space-y-2">
            {params.map((p) => (
              <button
                type="button"
                key={p.key}
                onClick={() => openBottomSheet(p)}
                className="w-full flex items-center justify-between gap-3 p-4 bg-white rounded-xl border border-gray-100 text-left shadow-sm"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {p.name}
                    <button className="ml-1 transform translate-y-[2px] p-0.5 rounded-full">
                      <Info className="w-4 h-4 text-gray-500" />
                    </button>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {p.unit && `Unit: ${p.unit}`}
                    {p.unit && p.rangeLabel ? " • " : ""}
                    {p.rangeLabel ? `Range: ${p.rangeLabel}` : ""}
                  </p>
                </div>
                <span
                  className={`text-lg font-semibold shrink-0 ${valueColorClass(
                    p.severityCode
                  )}`}
                >
                  {typeof p.value === "number" && Number.isFinite(p.value)
                    ? p.value % 1 === 0
                      ? p.value
                      : p.value.toFixed(2)
                    : "—"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bottom sheet: parameter description & unit, no image */}
      {bottomSheetOpen && selectedParam && (
        <BottomSheet
          show={bottomSheetOpen}
          title={selectedParam.name}
          showCloseButton={true}
          handleClose={() => setBottomSheetOpen(false)}
        >
          <div className="p-4">
            <p className="text-base  text-gray-700 mb-1">
              <span className="font-semibold">Unit:</span>{" "}
              {selectedParam.unit || "—"}
            </p>
            {selectedParam.description ? (
              <p className="text-sm text-gray-600 mt-2">
                {selectedParam.description}
              </p>
            ) : null}
            {selectedParam.ranges?.length ? (
              <Alert
                className="mt-3"
                title="Ranges"
                description={
                  <div className="mt-3">
                    <ul className="text-xs text-gray-600 space-y-0.5">
                      {selectedParam.ranges.map((r, i) => (
                        <li key={i}>
                          {r.label}: {Number(r.from).toFixed(2)}{" "}
                          {selectedParam.unit} – {Number(r.to).toFixed(2)}{" "}
                          {selectedParam.unit}
                        </li>
                      ))}
                    </ul>
                  </div>
                }
                variant="info"
                icon={<Info className="w-4 h-4" />}
              />
            ) : null}
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

export default DeviceResult;
